import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Building2, Search, Plus, Phone, Mail, Wrench, Trash2, Upload, Check, X, Pencil, GitMerge } from "lucide-react";

const EMPTY_FORM = { ragione_sociale: "", referente: "", telefono: "", email: "", indirizzo: "", piva: "", note: "" };

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [conMacchine, setConMacchine] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // { rows, mapping, fileName }
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // Merge mode
  const [mergeMode, setMergeMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [masterId, setMasterId] = useState("");
  const [merging, setMerging] = useState(false);

  const load = async (opts = {}) => {
    const cm = opts.conMacchine !== undefined ? opts.conMacchine : conMacchine;
    const params = {};
    if (q) params.q = q;
    if (cm) params.con_macchine = 1;
    const { data } = await api.get("/customers", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleConMacchine = () => {
    const nv = !conMacchine;
    setConMacchine(nv);
    load({ conMacchine: nv });
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setCreating(true);
    setForm({ ragione_sociale: c.ragione_sociale || "", referente: c.referente || "", telefono: c.telefono || "", email: c.email || "", indirizzo: c.indirizzo || "", piva: c.piva || "", note: c.note || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => { setCreating(false); setEditingId(null); setForm(EMPTY_FORM); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/customers/${editingId}`, form);
        toast.success("Cliente aggiornato");
      } else {
        await api.post("/customers", form);
        toast.success("Cliente creato");
      }
      closeForm(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare cliente?")) return;
    await api.delete(`/customers/${id}`); load();
  };

  // ----- Merge duplicati -----
  const toggleSelect = (id) => {
    setSelected(prev => {
      const nv = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (!nv.includes(masterId)) setMasterId(nv[0] || "");
      if (nv.length > 0 && !masterId) setMasterId(nv[0]);
      return nv;
    });
  };

  const doMerge = async () => {
    if (selected.length < 2 || !masterId) return;
    const master = items.find(i => i.id === masterId);
    const sources = selected.filter(id => id !== masterId);
    if (!window.confirm(`Unire ${sources.length} ${sources.length === 1 ? "duplicato" : "duplicati"} in "${master?.ragione_sociale}"?\n\nMacchine e schede verranno spostate sul cliente principale e i duplicati eliminati. Operazione irreversibile.`)) return;
    setMerging(true);
    try {
      const { data } = await api.post("/customers/merge", { target_id: masterId, source_ids: sources });
      toast.success(`Uniti ${data.merged} clienti: ${data.machines_moved} macchine e ${data.work_orders_moved} schede spostate`);
      setMergeMode(false); setSelected([]); setMasterId("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMerging(false); }
  };

  // ----- Import file XLSX/CSV -----
  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const data = await f.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      if (rows.length === 0) { toast.error("File vuoto"); return; }
      const cols = Object.keys(rows[0]);
      const findCol = (patterns) => cols.find(c => patterns.some(p => new RegExp(p, "i").test(c))) || "";
      const mapping = {
        ragione_sociale: findCol(["ragione", "nome.*cliente", "denominazione", "cliente"]) || cols[0],
        referente: findCol(["referente", "contatto", "titolare"]),
        telefono: findCol(["telefono", "phone", "tel", "cellulare"]),
        email: findCol(["email", "e-mail", "mail", "pec"]),
        indirizzo: findCol(["indirizzo", "via", "address"]),
        piva: findCol(["p.iva", "piva", "partita.iva", "cf", "codice.fisc"]),
      };
      setImportPreview({ rows, mapping, cols, fileName: f.name });
    } catch (err) { toast.error("Errore lettura file: " + err.message); }
    e.target.value = "";
  };

  const confirmImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const { rows, mapping } = importPreview;
      const customers = rows
        .map(r => ({
          ragione_sociale: String(r[mapping.ragione_sociale] || "").trim(),
          referente: String(r[mapping.referente] || "").trim(),
          telefono: String(r[mapping.telefono] || "").trim(),
          email: String(r[mapping.email] || "").trim(),
          indirizzo: String(r[mapping.indirizzo] || "").trim(),
          piva: String(r[mapping.piva] || "").trim(),
          note: "",
        }))
        .filter(c => c.ragione_sociale);
      if (customers.length === 0) { toast.error("Nessuna riga valida — verifica la colonna Ragione Sociale"); setImporting(false); return; }
      const { data } = await api.post("/customers/import", { customers });
      toast.success(`Import: ${data.created} creati, ${data.skipped_duplicates} duplicati saltati (su ${data.total})`);
      setImportPreview(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const sources = selected.filter(id => id !== masterId);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Anagrafica</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">Clienti</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={()=>{ setMergeMode(!mergeMode); setSelected([]); setMasterId(""); }} className={`h-11 px-4 border uppercase text-xs tracking-widest font-bold rounded-sm flex items-center gap-2 ${mergeMode ? "border-[#FF9F0A] text-[#FF9F0A] bg-[#FF9F0A]/10" : "border-zinc-700 text-zinc-300 hover:border-[#FF9F0A] hover:text-[#FF9F0A]"}`} data-testid="customer-merge-mode">
            <GitMerge size={14} /> {mergeMode ? "Annulla unione" : "Unisci duplicati"}
          </button>
          <label className="h-11 px-4 border border-zinc-700 hover:border-[#4CAF50] text-zinc-300 hover:text-[#4CAF50] uppercase text-xs tracking-widest font-bold rounded-sm flex items-center gap-2 cursor-pointer" data-testid="customer-import-button">
            <Upload size={14} /> Importa CSV/XLSX
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onImportFile} />
          </label>
          <button onClick={()=>{ creating ? closeForm() : setCreating(true); }} className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="customer-new-button">
            <Plus size={16} /> Nuovo cliente
          </button>
        </div>
      </div>

      {/* Merge panel */}
      {mergeMode && (
        <div className="gm-card p-5 mb-5 border-[#FF9F0A]/40 bg-[#FF9F0A]/5" data-testid="customer-merge-panel">
          <div className="text-xs uppercase tracking-widest text-[#FF9F0A] font-bold mb-1">Unione clienti duplicati</div>
          <div className="text-sm text-zinc-400 mb-3">
            Seleziona 2 o più clienti che sono la stessa impresa (es. "Iannizzotto" e "Antonio Iannizzotto"), scegli quello <b className="text-white">principale</b> da mantenere, poi conferma. Macchine e schede dei duplicati passeranno al principale.
          </div>
          {selected.length === 0 ? (
            <div className="text-xs text-zinc-500">Nessun cliente selezionato — spunta le caselle sulle card qui sotto.</div>
          ) : (
            <div className="space-y-1.5 mb-3">
              {selected.map(id => {
                const c = items.find(i => i.id === id);
                if (!c) return null;
                return (
                  <label key={id} className={`flex items-center gap-3 p-2.5 border cursor-pointer ${masterId === id ? "border-[#4CAF50] bg-[#4CAF50]/5" : "border-zinc-800"}`} data-testid={`merge-master-option-${id}`}>
                    <input type="radio" name="master" checked={masterId === id} onChange={()=>setMasterId(id)} className="accent-[#4CAF50]" />
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold">{c.ragione_sociale}</span>
                      <span className="text-xs text-zinc-500 ml-2">{c.machine_count || 0} macchine {c.piva && `• ${c.piva}`}</span>
                    </div>
                    {masterId === id ? <span className="gm-badge gm-badge-success shrink-0">PRINCIPALE (resta)</span> : <span className="gm-badge gm-badge-danger shrink-0">DUPLICATO (eliminato)</span>}
                  </label>
                );
              })}
            </div>
          )}
          <button onClick={doMerge} disabled={selected.length < 2 || !masterId || merging} className="gm-btn-primary h-11 px-6 rounded-sm flex items-center gap-2 disabled:opacity-40" data-testid="customer-merge-confirm">
            <GitMerge size={16} /> {merging ? "Unione in corso..." : `Unisci ${Math.max(sources.length, 0)} ${sources.length === 1 ? "duplicato" : "duplicati"}`}
          </button>
        </div>
      )}

      {/* Import mapping preview */}
      {importPreview && (
        <div className="gm-card p-5 mb-5 border-[#4CAF50]/40" data-testid="customer-import-preview">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4CAF50] font-bold">Anteprima import</div>
              <h3 className="font-display text-xl font-bold uppercase">{importPreview.fileName}</h3>
              <div className="text-xs text-zinc-400 mt-1">{importPreview.rows.length} righe trovate. Mappa le colonne del tuo file:</div>
            </div>
            <button onClick={()=>setImportPreview(null)} className="text-zinc-400 hover:text-white p-1"><X size={20}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {["ragione_sociale","referente","telefono","email","indirizzo","piva"].map(field => (
              <div key={field}>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex justify-between">
                  {field.replaceAll("_"," ")} {field==="ragione_sociale" && <span className="text-[#4CAF50]">*obbligatoria</span>}
                </label>
                <select value={importPreview.mapping[field]} onChange={e=>setImportPreview({...importPreview, mapping:{...importPreview.mapping, [field]: e.target.value}})} className="gm-input" data-testid={`import-map-${field}`}>
                  <option value="">— nessuna —</option>
                  {importPreview.cols.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-800 pt-3 mb-3">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Anteprima prime 3 righe (dopo mapping)</div>
            <div className="overflow-x-auto">
              <table className="gm-table text-xs">
                <thead><tr><th>Ragione sociale</th><th>Referente</th><th>Tel</th><th>Email</th><th>P.IVA</th></tr></thead>
                <tbody>
                  {importPreview.rows.slice(0,3).map((r,i) => (
                    <tr key={i}>
                      <td className="font-semibold">{r[importPreview.mapping.ragione_sociale] || "—"}</td>
                      <td>{r[importPreview.mapping.referente] || "—"}</td>
                      <td className="font-mono-data">{r[importPreview.mapping.telefono] || "—"}</td>
                      <td className="text-zinc-400">{r[importPreview.mapping.email] || "—"}</td>
                      <td className="font-mono-data">{r[importPreview.mapping.piva] || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={confirmImport} disabled={importing || !importPreview.mapping.ragione_sociale} className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="customer-import-confirm">
              <Check size={16} /> {importing ? "Import in corso..." : `Importa ${importPreview.rows.length} clienti`}
            </button>
            <button onClick={()=>setImportPreview(null)} className="h-11 px-5 border border-zinc-700 uppercase text-xs tracking-widest font-bold">Annulla</button>
          </div>
        </div>
      )}

      {creating && (
        <form onSubmit={submit} className={`gm-card p-5 mb-5 grid grid-cols-1 md:grid-cols-2 gap-3 ${editingId ? "border-[#4CAF50]/40" : ""}`} data-testid="customer-form">
          {editingId && (
            <div className="md:col-span-2 flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-[#4CAF50] font-bold flex items-center gap-1"><Pencil size={12}/> Modifica anagrafica cliente</span>
              <button type="button" onClick={closeForm} className="text-zinc-500 hover:text-white"><X size={16}/></button>
            </div>
          )}
          <input required placeholder="Ragione sociale *" value={form.ragione_sociale} onChange={e=>setForm(s=>({...s,ragione_sociale:e.target.value}))} className="gm-input md:col-span-2" data-testid="customer-rs" />
          <input placeholder="Referente" value={form.referente} onChange={e=>setForm(s=>({...s,referente:e.target.value}))} className="gm-input" data-testid="customer-referente" />
          <input placeholder="P.IVA" value={form.piva} onChange={e=>setForm(s=>({...s,piva:e.target.value}))} className="gm-input font-mono-data" data-testid="customer-piva" />
          <input placeholder="Telefono" value={form.telefono} onChange={e=>setForm(s=>({...s,telefono:e.target.value}))} className="gm-input" data-testid="customer-telefono" />
          <input placeholder="Email" type="email" value={form.email} onChange={e=>setForm(s=>({...s,email:e.target.value}))} className="gm-input" data-testid="customer-email" />
          <input placeholder="Indirizzo" value={form.indirizzo} onChange={e=>setForm(s=>({...s,indirizzo:e.target.value}))} className="gm-input md:col-span-2" data-testid="customer-indirizzo" />
          <button type="submit" className="gm-btn-primary h-11 rounded-sm md:col-span-2" data-testid="customer-submit">{editingId ? "Salva modifiche" : "Crea cliente"}</button>
        </form>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&load()} placeholder="Cerca per nome, P.IVA, email..." className="gm-input pl-11" data-testid="customer-search" />
        </div>
        <button onClick={()=>load()} className="h-12 px-6 border border-zinc-700 hover:border-white uppercase text-xs tracking-widest font-bold">Cerca</button>
        <button onClick={toggleConMacchine} className={`h-12 px-4 border uppercase text-xs tracking-widest font-bold flex items-center gap-2 ${conMacchine ? "bg-[#4CAF50] text-white border-[#4CAF50]" : "border-zinc-700 text-zinc-400 hover:text-white"}`} data-testid="customer-filter-macchine">
          <Wrench size={14} /> Con macchine
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 && <div className="text-zinc-500">Nessun cliente{conMacchine ? " con macchine" : ""}. {!conMacchine && "Crea il primo cliente per iniziare."}</div>}
        {items.map(c => (
          <div key={c.id} className={`gm-card p-4 flex items-start gap-3 ${mergeMode && selected.includes(c.id) ? "border-[#FF9F0A]/60 bg-[#FF9F0A]/5" : ""}`} data-testid="customer-card">
            {mergeMode && (
              <input type="checkbox" checked={selected.includes(c.id)} onChange={()=>toggleSelect(c.id)} className="mt-1 h-5 w-5 accent-[#FF9F0A] shrink-0" data-testid={`merge-check-${c.id}`} />
            )}
            <div className="h-12 w-12 bg-[#4CAF50]/10 border border-[#4CAF50]/30 flex items-center justify-center shrink-0">
              <Building2 className="text-[#4CAF50]" size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg font-bold uppercase truncate">{c.ragione_sociale}</div>
              <div className="text-xs text-zinc-400">{c.referente || "—"} {c.piva && <span className="font-mono-data ml-2 text-zinc-500">{c.piva}</span>}</div>
              <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-3">
                {c.telefono && <span className="flex items-center gap-1"><Phone size={11} /> {c.telefono}</span>}
                {c.email && <span className="flex items-center gap-1"><Mail size={11} /> {c.email}</span>}
              </div>
              <div className="mt-3 flex gap-3 flex-wrap items-center">
                <Link to={`/macchine?customer=${c.id}`} className="text-xs uppercase tracking-widest text-[#4CAF50] hover:underline flex items-center gap-1">
                  <Wrench size={12} /> Macchine {(c.machine_count || 0) > 0 && <span className="gm-badge gm-badge-success">{c.machine_count}</span>}
                </Link>
                <Link to={`/schede?customer=${c.id}`} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white">Schede</Link>
              </div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button onClick={()=>openEdit(c)} className="text-zinc-400 hover:text-[#4CAF50] hover:bg-[#4CAF50]/10 p-1.5" title="Modifica anagrafica" data-testid={`customer-edit-${c.id}`}><Pencil size={14}/></button>
              <button onClick={()=>del(c.id)} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-1.5" title="Elimina" data-testid={`customer-delete-${c.id}`}><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
